import { useEffect, useState, useMemo, Fragment } from "react";
import { useApp } from "contexts/AppContext";
import config from "config";
import axios from "axios";
import { getEntryByContentTypeAndLocale } from "api/contentful";
import {
  Checkbox,
  Stack,
  FormControlLabel,
  Typography,
  Link,
  Container,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
} from '@newulife/common'
import { lowerCase, startCase, isEmpty, find } from "lodash";
import { mockApiCall } from "helpers/mockApiCall";
import { devToolsState } from "state";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { RichText } from "modules/RichText";
import { useContentfulContext } from "hooks/useContentful";
import terms from "api/terms";

const TermsVerification = () => {
  const { title, description, inputModelArray } = useContentfulContext({
    entryPoint: "fields",
  });
  const [termsData, setTermsData] = useState(null)
  const [requiredOptions, setRequiredOptions] = useState([])
  const [optionalOptions, setOptionalOptions] = useState([])
  const [saveApiError, setSaveApiError] = useState(false);
  const [saveApiIsLoading, setSaveApiIsLoading] = useState(false);
  const [saveApiErrorCode, setSaveApiErrorCode] = useState(false);
  const [devToolsSettings, setDevToolsSettings] = useRecoilState(devToolsState);
  const [form, onSetForm] = useState({
    terms: [],
  })
  const {
    state: { rep, locale },
  } = useApp();
  const { search } = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(search.replace(/&amp;/g, "&")),
    [search]
  );
  const ARTemplateID = searchParams.get("artemplateid");

  const handleChange = (idx, idx1, field) => {
    const temp = [...form.terms]
    const index = temp.findIndex(o => o.TermId === field?.TermId)
    if (index > -1) {
      temp[index] = {
        ...temp[index],
        IsAgreedTo: !temp[index]?.IsAgreedTo,
      }
    } else {
      temp.push({
        TermId: field?.TermId,
        IsAgreedTo: true,
      })
    }
    onSetForm({
      ...form,
      terms: temp,
    })
  }

  const onSave = async () => {
    setSaveApiIsLoading(true)
    //devTools Values for Error
    const devToolsError =
      devToolsSettings.disableVerificationRedirect &&
      devToolsSettings.verificationSubmissionResult === "error"

    // submit the data
    try {
      let res = {};
      if (devToolsError) {
        res = await mockApiCall("post-autoresponder-details")
      } else {
        res = await axios.post(
          `${config.REACT_APP_API_URL}/api/profile/autoresponder-details`,
          {
            ...form,
            ARTemplateID,
          },
          {
            headers: {
              Authorization: `Bearer ${rep?.token}`,
            },
          }
        )
        if (!devToolsSettings.disableVerificationRedirect) {
          if (res.data?.url) {
            setSaveApiError(false)
            setSaveApiIsLoading(false)
          }  
          window.location.replace(res.data?.url);
        }
      }
    } catch (error) {
      setSaveApiError(saveApiError + 1)
      setSaveApiErrorCode(error.response.data.id)
      setSaveApiIsLoading(false)
      console.error(
        "Error during the save validation:",
        error.response ? error.response.data.message : error.response.data
      );
    }
  }

  useEffect(() => {
    const asyncFunc = async () => {
      const country = rep.country === 'USA' ? rep.country : startCase(lowerCase(rep.country));
      // const marketingTerms = await getEntryByContentTypeAndLocale('enrollmentTermsComponent', locale, country, 'Marketing');
      const res = await terms.post(rep.DefaultLocaleID || 1, country)
      const data = await getEntryByContentTypeAndLocale('enrollmentTermsComponent', locale, country, 'Terms')

      const requiredCheckboxItems = data?.fields?.requiredCheckboxItems?.map?.(o => {
        let temp = {}
        res?.forEach(o1 => {
          if (o1.AreaId === o?.fields?.byDesignAreaId) temp = o1
        })
        if (isEmpty(temp)) return null
        return {
          ...temp,
          ...o.fields,
        }
      })?.filter(Boolean) || []

      const optionalCheckboxItems = data?.fields?.optionalCheckboxItems?.map?.(o => {
        let temp = {}
        res?.forEach(o1 => {
          if (o1.AreaId === o?.fields?.byDesignAreaId) temp = o1
        })
        if (isEmpty(temp)) return null
        return {
          ...temp,
          ...o.fields,
        }
      })?.filter(Boolean) || []

      setRequiredOptions(requiredCheckboxItems)
      setOptionalOptions(optionalCheckboxItems)
      setTermsData(data)
    }
    if (!isEmpty(rep)) asyncFunc()
  }, [locale, rep])

  return (
    <Stack sx={{ width: '100%' }}>
      <Container>
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: "700px",
            mx: "auto",
            position: 'relative',
          }}
        >
          <Stack
            display="grid"
            p={{
              xs: 2,
              sm: 3,
            }}
            gap={4}
          >
            <Stack
              display="grid"
              gap={2}
              justifyContent="center"
              justifyItems="center"
              justifySelf="center"
              textAlign="center"
              maxWidth="500px"
            >
              <Typography
                variant="header1"
                fontWeight="400"
                fontSize="24px"
                lineHeight="28px"
                color="neutralCool.dark"
              >
                {title}
              </Typography>
              <Typography variant="body3">
                {description}
              </Typography>
            </Stack>
            <div className="form-max flex-20 column">
              {[requiredOptions, optionalOptions].map((o, idx) => (
                (o?.length > 0) && (
                  <Fragment
                    key={`termsSection${idx}`}
                  >
                    {(idx === 0 && termsData?.fields?.requiredSectionText && requiredOptions?.length > 0) && (
                      <Typography variant="body1">
                        {termsData?.fields?.requiredSectionText}
                      </Typography>
                    )}
                    {(idx === 1 && termsData?.fields?.optionalSectionText && optionalOptions?.length > 0) && (
                      <Typography variant="body1">
                        {termsData?.fields?.optionalSectionText}
                      </Typography>
                    )}

                    {o?.map?.((field, idx1) => {
                      // For marketing terms (-8 email, -9 phone), replace placeholders with rep's info
                      let displayLabel = field?.checkboxLabel;
                      const isEmailTerm = field?.TermId === -8 || field?.AreaId === -8;
                      const isPhoneTerm = field?.TermId === -9 || field?.AreaId === -9;
                      if (displayLabel && isEmailTerm) {
                        displayLabel = rep?.email
                          ? displayLabel.replace(/\{\{email\}\}/g, rep.email)
                          : displayLabel.trim().split(/\s+/)[0] || displayLabel;
                      }
                      if (displayLabel && isPhoneTerm) {
                        displayLabel = rep?.phone1
                          ? displayLabel.replace(/\{\{phone\}\}/g, rep.phone1)
                          : displayLabel.trim().split(/\s+/)[0] || displayLabel;
                      }
                      return (
                      <FormControlLabel
                        key={`terms${idx}${idx1}`}
                        sx={{
                          alignItems: 'flex-start',
                        }}
                        control={
                          <Checkbox
                            className="js-check"
                            id={field?.TermId.toString()}
                            name={field?.TermId.toString()}
                            checked={!!find(form.terms, { TermId: field?.TermId })?.IsAgreedTo}
                            onChange={() => handleChange(idx, idx1, field)}
                          />
                        }
                        label={
                          <Stack gap={1} pt={0.3}>
                            {displayLabel && (
                              <Typography
                                as="label"
                                variant="body3"
                                htmlFor={field?.TermId}
                              >
                                {displayLabel}
                              </Typography>
                            )}
                            {(field?.DocumentURL || field?.linkedFile?.fields?.file?.url) ? (
                              <Link
                                href={(field?.DocumentURL || field?.linkedFile?.fields?.file?.url)}
                                target="_blank"
                                underline="hover"
                                sx={{
                                  fontWeight: 500,
                                  display: 'block',
                                }}
                              >
                                {(field?.LinkLabel || field?.linkLabel)}
                              </Link>
                            ) : (field?.useModal || field?.modalText) ? (
                              <Link
                                underline="hover"
                                sx={{
                                  fontWeight: 500,
                                  display: 'block',
                                }}
                                onClick={e => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  // setModalOpen({ open: true, modalProps: field })
                                }}
                              >
                                {(field?.LinkLabel || field?.linkLabel)}
                              </Link>
                            ) : null}
                          </Stack>
                        }
                      />
                    );})}
                  </Fragment>
                )
              ))}
              <Button
                sx={{ mt: 2 }}
                size="large"
                disabled={requiredOptions?.some?.(o => !find(form.terms, { TermId: o?.TermId })?.IsAgreedTo) || saveApiIsLoading}
                onClick={onSave}
              >
                {inputModelArray?.[1]?.fields?.title}
              </Button>
            </div>
            {(saveApiError && !saveApiIsLoading) && (
              <Alert
                severity="error"
                variant="standard"
                className="alert"
              >
                <Grid container gap={1} direction="column">
                  <Typography align="left" variant="body2">
                    {inputModelArray?.[2]?.fields?.title}
                  </Typography>
                  {saveApiError >= 3 && (
                    <Typography align="left" variant="subhead1">
                      <RichText text={ inputModelArray?.[2]?.fields?.richText } />
                    </Typography>
                  )}
                  {saveApiErrorCode && (
                    <Typography align="left" variant="label2">
                      {inputModelArray?.[2]?.fields?.item1} {saveApiErrorCode}
                    </Typography>
                  )}
                </Grid>
              </Alert>
            )}
          </Stack>
        </Paper>
      </Container>
      {saveApiIsLoading && (
        <Stack
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            bgcolor: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          <CircularProgress
            size="lg"
            sx={{
              m: 'auto',
            }}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default TermsVerification;